document.documentElement.classList.add('vr-js');

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class VertiReliefStepTabs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.tabs = [...this.querySelectorAll('[data-vr-step-tab]')];
    this.panels = [...this.querySelectorAll('[data-vr-step-panel]')];
    this.progressBar = this.querySelector('[data-vr-step-progress]');
    this.duration = Math.max(Number(this.dataset.duration) || 8000, 1000);
    this.autoplay = this.dataset.autoplay === 'true' && !reducedMotion.matches && this.tabs.length > 1;
    this.activeIndex = Math.max(this.tabs.findIndex((tab) => tab.classList.contains('is-active')), 0);
    this.pauseReasons = new Set();
    this.progress = 0;
    this.pointerStart = null;
    this.classList.add('is-enhanced');

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index, true));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    this.querySelectorAll('[data-vr-video-toggle]').forEach((button) => {
      button.addEventListener('click', () => this.toggleVideo(button));
    });

    this.addEventListener('focusin', () => this.pause('focus'));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resume('focus');
    });

    if (this.dataset.pauseHover === 'true' && window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('pointerenter', () => this.pause('hover'));
      this.addEventListener('pointerleave', () => this.resume('hover'));
    }

    const panelRegion = this.querySelector('[data-vr-step-panels]');
    panelRegion?.addEventListener('pointerdown', (event) => {
      this.pointerStart = { x: event.clientX, y: event.clientY, time: performance.now() };
      this.pause('touch');
    }, { passive: true });
    panelRegion?.addEventListener('pointerup', (event) => this.onPointerEnd(event), { passive: true });
    panelRegion?.addEventListener('pointercancel', () => {
      this.pointerStart = null;
      this.resume('touch');
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) this.resume('offscreen');
        else this.pause('offscreen');
      }, { threshold: 0.2 });
      this.visibilityObserver.observe(this);
    }

    this.onVisibilityChange = () => document.hidden ? this.pause('document') : this.resume('document');
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.onMotionChange = (event) => {
      this.autoplay = this.dataset.autoplay === 'true' && !event.matches && this.tabs.length > 1;
      if (this.autoplay) this.restartTimer();
      else this.stopTimer();
    };
    reducedMotion.addEventListener?.('change', this.onMotionChange);

    this.select(this.activeIndex, false);
  }

  disconnectedCallback() {
    this.stopTimer();
    this.visibilityObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    reducedMotion.removeEventListener?.('change', this.onMotionChange);
  }

  onKeydown(event, index) {
    let target = null;
    if (event.key === 'ArrowRight') target = (index + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') target = (index - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') target = 0;
    if (event.key === 'End') target = this.tabs.length - 1;
    if (target === null) return;
    event.preventDefault();
    this.select(target, true);
    this.tabs[target]?.focus();
  }

  onPointerEnd(event) {
    if (!this.pointerStart) return;
    const deltaX = event.clientX - this.pointerStart.x;
    const deltaY = event.clientY - this.pointerStart.y;
    const elapsed = performance.now() - this.pointerStart.time;
    this.pointerStart = null;
    this.resume('touch');
    if (elapsed < 700 && Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.25) {
      const direction = deltaX < 0 ? 1 : -1;
      this.select((this.activeIndex + direction + this.tabs.length) % this.tabs.length, true);
    }
  }

  select(index, userInitiated) {
    if (!this.tabs.length || !this.panels.length) return;
    this.activeIndex = Math.min(Math.max(index, 0), this.tabs.length - 1);
    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === this.activeIndex;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === this.activeIndex;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
      const video = panel.querySelector('video');
      const toggle = panel.querySelector('[data-vr-video-toggle]');
      if (!video) return;
      if (active) {
        video.muted = true;
        video.playsInline = true;
        video.play().then(() => {
          if (toggle) toggle.dataset.playing = 'true';
        }).catch(() => {
          if (toggle) toggle.dataset.playing = 'false';
        });
      } else {
        video.pause();
        if (toggle) toggle.dataset.playing = 'false';
      }
    });
    const activeTab = this.tabs[this.activeIndex];
    if (userInitiated) activeTab?.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
    this.restartTimer();
  }

  toggleVideo(button) {
    const video = button.closest('[data-vr-step-panel]')?.querySelector('video');
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
      button.dataset.playing = 'true';
      this.resume('video');
    } else {
      video.pause();
      button.dataset.playing = 'false';
      this.pause('video');
    }
  }

  pause(reason) {
    this.pauseReasons.add(reason);
    this.stopTimer(false);
  }

  resume(reason) {
    this.pauseReasons.delete(reason);
    if (this.pauseReasons.size === 0 && this.autoplay) this.startTimer();
  }

  restartTimer() {
    this.progress = 0;
    if (this.progressBar) this.progressBar.style.transform = 'scaleX(0)';
    this.stopTimer(false);
    if (this.autoplay && this.pauseReasons.size === 0) this.startTimer();
  }

  startTimer() {
    if (!this.autoplay || this.frame) return;
    this.startedAt = performance.now() - this.progress * this.duration;
    const tick = (now) => {
      this.progress = Math.min((now - this.startedAt) / this.duration, 1);
      if (this.progressBar) this.progressBar.style.transform = `scaleX(${this.progress})`;
      if (this.progress >= 1) {
        this.frame = null;
        this.select((this.activeIndex + 1) % this.tabs.length, false);
        return;
      }
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  stopTimer(reset = false) {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
    if (reset) this.progress = 0;
  }
}

class VertiReliefComparisonTabs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.tabs = [...this.querySelectorAll('[data-vr-comparison-tab]')];
    this.panels = [...this.querySelectorAll('[data-vr-comparison-panel]')];
    this.classList.add('is-enhanced');
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index, true));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });
    this.select(0, false);
  }

  onKeydown(event, index) {
    let target = null;
    if (event.key === 'ArrowRight') target = (index + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') target = (index - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') target = 0;
    if (event.key === 'End') target = this.tabs.length - 1;
    if (target === null) return;
    event.preventDefault();
    this.select(target, true);
    this.tabs[target]?.focus();
  }

  select(index, userInitiated) {
    if (!this.tabs.length || !this.panels.length) return;
    const nextIndex = Math.min(Math.max(index, 0), this.tabs.length - 1);
    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === nextIndex;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== nextIndex;
    });
    if (userInitiated) this.tabs[nextIndex]?.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
  }
}

if (!customElements.get('vr-step-tabs')) customElements.define('vr-step-tabs', VertiReliefStepTabs);
if (!customElements.get('vr-comparison-tabs')) customElements.define('vr-comparison-tabs', VertiReliefComparisonTabs);

class VertiReliefTheme {
  constructor(root = document) {
    this.root = root;
    this.bindSliders();
    this.bindReveals();
    this.bindStickyOffer();
  }

  bindSliders() {
    this.root.querySelectorAll?.('[data-vr-slider]').forEach((section) => {
      if (section.dataset.vrBound === 'true') return;
      section.dataset.vrBound = 'true';
      section.addEventListener('click', (event) => {
        const button = event.target.closest('[data-vr-slider-button]');
        if (!button) return;
        const track = section.querySelector('[data-vr-slider-track]');
        if (!track) return;
        const direction = button.dataset.direction === 'previous' ? -1 : 1;
        const distance = Math.max(track.clientWidth * 0.82, 280);
        track.scrollBy({ left: distance * direction, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      });
    });
  }

  bindReveals() {
    const elements = [...(this.root.querySelectorAll?.('[data-vr-reveal]') || [])];
    if (!elements.length) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    elements.forEach((element) => observer.observe(element));
  }

  bindStickyOffer() {
    const bar = document.querySelector('[data-vr-mobile-purchase]');
    const offer = document.getElementById('VertiReliefOfferZone');
    if (!bar || !offer || bar.dataset.vrBound === 'true' || !('IntersectionObserver' in window)) return;
    bar.dataset.vrBound = 'true';
    const observer = new IntersectionObserver(([entry]) => {
      bar.hidden = entry.isIntersecting;
    }, { rootMargin: '-10% 0px -55% 0px', threshold: 0.05 });
    observer.observe(offer);
  }
}

const initVertiRelief = (root = document) => new VertiReliefTheme(root);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initVertiRelief(), { once: true });
else initVertiRelief();

document.addEventListener('shopify:section:load', (event) => initVertiRelief(event.target));
